import type { ListFeedbackResponse } from '@shared/schemas';
import { all, call, getContext, put, takeEvery, takeLatest } from 'redux-saga/effects';

import { classifyInfraError } from '@/utils/infra-error';
import { logger } from '@/utils/logger';

import type { ListFeedbacksGateway } from '../infrastructure/list-feedbacks.gateway';
import { feedbacksRequested, openCountRequested, statusChangeRequested } from './list-feedbacks.actions';
import { listFeedbacksActions } from './list-feedbacks.slice';

/** WORKER: the table (drives the page's spinner and error). */
function* loadWorker() {
  const gateway: ListFeedbacksGateway = yield getContext('listFeedbacksGateway');
  yield put(listFeedbacksActions.loadStarted());
  try {
    const feedbacks: ListFeedbackResponse[] = yield call(gateway.loadFeedbacks);
    yield put(listFeedbacksActions.loadSucceeded(feedbacks));
  } catch {
    yield put(listFeedbacksActions.loadFailed());
  }
}

/**
 * WORKER: the sidebar badge. Silent on failure: the badge is a hint on every
 * admin page, and an error banner there would fire on pages that have nothing
 * to do with feedbacks. The last known count stays.
 */
function* openCountWorker() {
  const gateway: ListFeedbacksGateway = yield getContext('listFeedbacksGateway');
  try {
    const count: number = yield call(gateway.loadOpenCount);
    yield put(listFeedbacksActions.openCountLoaded(count));
  } catch (e) {
    logger.warn('Could not load the open feedback count', e);
  }
}

/** WORKER: one status change, then the badge, which it may have moved. */
function* statusChangeWorker(action: ReturnType<typeof statusChangeRequested>) {
  const gateway: ListFeedbacksGateway = yield getContext('listFeedbacksGateway');
  yield put(listFeedbacksActions.statusUpdateStarted(action.payload.id));
  try {
    const updated: ListFeedbackResponse = yield call(
      gateway.updateStatus,
      action.payload.id,
      action.payload.status,
    );
    yield put(listFeedbacksActions.statusUpdated(updated));
    yield put(openCountRequested());
  } catch (e) {
    yield put(
      listFeedbacksActions.statusUpdateFailed(
        classifyInfraError(e, 'Could not change the status.').message,
      ),
    );
  }
}

/** WATCHERS. */
export function* listFeedbacksSaga() {
  yield all([
    takeLatest(feedbacksRequested.type, loadWorker),
    takeLatest(openCountRequested.type, openCountWorker),
    takeEvery(statusChangeRequested.type, statusChangeWorker),
  ]);
}
